/**
 * RaceTrace Live: the field on the track for the whole live page (live-field.ts), so every tab can
 * use it and the track map shows it at once when opened. It loads the circuit, reads the cars'
 * positions every 5 s while it is enabled and the page is visible, moves each car on between
 * readings, and keeps who is in the pit lane, off the track and stopped, and the last flag change.
 *
 * The map moves the cars every frame (stepTo); with the map closed the hook moves them each second.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type { RaceChartData } from "@shared/types";
import { getPublic } from "../../hooks/useLiveChartData";
import { closingOnFocus, type Closing } from "./closing";
import { mapPaces } from "./live-battles";
import { stoppedByLaps } from "./live-strategy";
import { outCars, type PitVisit } from "./RunningOrder";
import type { TrackPlace } from "./LiveTrackOrder";
import {
  IN_PIT,
  OFF_TRACK,
  PIT_ANY,
  OVERDUE,
  SIG_LOST,
  step,
  takeReading,
  type CarMotion,
  type CarPlace,
  type PitSpot,
  type PitLane,
} from "./track-motion";
import {
  carPlaces,
  flagEvent,
  isCaution,
  lapPaceOf,
  pitVisitsNow,
  stoppedCars,
  typicalSpeedOf,
  type FlagChange,
  type TrackOutline,
} from "./live-field";

/** How often the positions are read (the server reads them every 5 s; the edge keeps them 2 s). */
export const POLL_MS = 5_000;
/** A frame's step is cut to this (ms), so a tab left in the background does not jump the cars. */
const FRAME_MAX_MS = 100;
/** Without the map, the cars are moved on each second. */
const TICK_MS = 1_000;

export interface LiveField {
  /** The circuit, once the timing has learned it; `trackMissing` until then. */
  track: TrackOutline | null;
  trackMissing: boolean;
  /** Each car's motion on the track, by published number (moved on in place: read, never replaced by a caller). */
  motions: MutableRefObject<Map<string, CarMotion>>;
  /** The pit lane on the lap, once the server has learned it from the pit flags. */
  pitLane: MutableRefObject<PitLane | null>;
  /** When the last reading was true (client clock). */
  readingAt: number | null;
  /** In the pit lane by the last reading (published numbers), and each car's visit (chart keys). */
  pits: string[];
  pitVisits: Map<number, PitVisit>;
  /**
   * The cars in the pits or on the pit road, and where: their place on the lap and, off the racing
   * line, their own point (the map draws them beside the track).
   */
  pitSpots: PitSpot[];
  /** No place in the last reading, and off the track (published numbers). */
  unplaced: string[];
  offTrack: string[];
  /** No usable signal from their in-car device; overdue with no position (not drawn: where they are is not known). */
  lostSignal: string[];
  overdue: string[];
  /** The cars not running (no lap for over 15 minutes): OUT. */
  out: Set<number>;
  /** The clock, each second. */
  now: number;
  /** A car's pace until its own is known (m/s). */
  typicalSpeed: number;
  /** Every car's place now (chart keys), and the cars in the pit lane. */
  places: TrackPlace[];
  /** Each car's recent green lap time (s), as the Trend column's. */
  greenPace: Map<number, number | null>;
  /** Stopped now (chart keys): one rule for every tab (live-field.ts stoppedCars). */
  stopped: Set<number>;
  /** The last flag change this page saw. */
  flagChange: FlagChange | null;
  /** The cars that will catch `key` within its next lap (closing.ts). */
  closingOn: (key: number) => Closing[];
  /** Move every car on to `t` (client clock), by at most `maxDtMs` (a frame's: 100 ms). */
  stepTo: (t: number, maxDtMs?: number) => void;
}

/**
 * A replay played in this browser (features/live/replay-field.ts): its circuit, and every car's
 * place at this device's time (the replay's clock), instead of the server's outline and readings.
 */
export interface ReplayFieldSource {
  /** The circuit; null when it has none (no map). */
  outline: TrackOutline | null;
  pit: PitLane | null;
  placesAt: (wallMs: number) => CarPlace[];
}

/** The replay's places as motions: each car exactly where its timing puts it, moved on each frame. */
function replayMotions(prev: Map<string, CarMotion>, places: CarPlace[], t: number, data: RaceChartData | null): Map<string, CarMotion> {
  const next = new Map<string, CarMotion>();
  for (const [num, d, mph, flags] of places) {
    if (d === null || flags & PIT_ANY) continue;
    const m = prev.get(num);
    const v = mph !== null && mph > 0 ? mph * 0.44704 : (m?.v ?? 40);
    const key = data ? Object.keys(data.cars).find((k) => (data.cars[k].label ?? String(data.cars[k].num)) === num) : undefined;
    const laps = key && data ? data.cars[key].laps.length : (m?.laps ?? 0);
    if (m) {
      m.d = d;
      m.raw = d;
      m.shown = d;
      m.at = t;
      m.rawSince = t;
      m.mph = mph;
      m.flags = flags;
      m.v = v;
      if (laps !== m.laps) m.lapAt = t;
      m.laps = laps;
      next.set(num, m);
    } else {
      next.set(num, { num, d, at: t, flags, mph, v, shown: d, raw: d, rawSince: t, base: v, fixes: [], far: 0, snap: true, laps, lapAt: t, born: t });
    }
  }
  return next;
}

export function useLiveField({
  slug,
  data,
  flag,
  enabled,
  replay = null,
}: {
  slug: string;
  data: RaceChartData | null;
  flag: string | null;
  enabled: boolean;
  /** A replay in this browser: its circuit and places, not the server's. */
  replay?: ReplayFieldSource | null;
}): LiveField {
  const [track, setTrack] = useState<TrackOutline | null>(null);
  const [trackMissing, setTrackMissing] = useState(false);
  const [readingAt, setReadingAt] = useState<number | null>(null);
  const [pits, setPits] = useState<string[]>([]);
  const pitLane = useRef<PitLane | null>(null);
  const [unplaced, setUnplaced] = useState<string[]>([]);
  const [offTrack, setOffTrack] = useState<string[]>([]);
  const [pitSpots, setPitSpots] = useState<PitSpot[]>([]);
  const [lostSignal, setLostSignal] = useState<string[]>([]);
  const [overdue, setOverdue] = useState<string[]>([]);
  const [pitVisits, setPitVisits] = useState<Map<number, PitVisit>>(() => new Map());
  const [now, setNow] = useState(() => Date.now());
  const motions = useRef(new Map<string, CarMotion>());
  const dataRef = useRef(data);
  dataRef.current = data;

  // A new race on the same page starts empty.
  useEffect(() => {
    setTrack(null);
    setTrackMissing(false);
    setReadingAt(null);
    setPits([]);
    setUnplaced([]);
    setOffTrack([]);
    setPitSpots([]);
    setLostSignal([]);
    setOverdue([]);
    setPitVisits(new Map());
    motions.current = new Map();
    pitLane.current = null;
  }, [slug]);

  // The outline: once enabled, and again every minute until the timing has learned the circuit.
  const loaded = useRef<string | null>(null);
  const replayRef = useRef(replay);
  replayRef.current = replay;
  const replayOutline = replay?.outline ?? null;
  const isReplay = replay !== null;
  useEffect(() => {
    if (!isReplay) return;
    setTrack(replayOutline);
    setTrackMissing(!replayOutline);
  }, [isReplay, replayOutline]);
  useEffect(() => {
    if (isReplay || !enabled || !slug || loaded.current === slug) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () =>
      getPublic<TrackOutline>(`/live/${encodeURIComponent(slug)}/track`)
        .then(({ body }) => {
          if (!alive) return;
          loaded.current = slug;
          setTrack(body);
          setTrackMissing(false);
        })
        .catch(() => {
          if (!alive) return;
          setTrackMissing(true);
          timer = setTimeout(load, 60_000);
        });
    load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [slug, enabled, isReplay]);

  const typicalSpeed = useMemo(
    () => (data ? typicalSpeedOf(data, track) : 40),
    [data, track]
  );
  // Read through refs so each reading uses the latest.
  const lapPace = useMemo(
    () => (data ? lapPaceOf(data, track, typicalSpeed) : new Map()),
    [data, track, typicalSpeed]
  );
  const lapPaceRef = useRef(lapPace);
  lapPaceRef.current = lapPace;
  // Under a caution cars really do slow: the motion allows their pace to fall further.
  const cautionRef = useRef(isCaution(flag));
  cautionRef.current = isCaution(flag);

  // A replay: every car's place from its timing, each second (the map moves them each frame).
  useEffect(() => {
    if (!isReplay || !enabled || !track) return;
    const read = () => {
      const r = replayRef.current;
      if (!r) return;
      const t = Date.now();
      const cars = r.placesAt(t);
      pitLane.current = r.pit;
      motions.current = replayMotions(motions.current, cars, t, dataRef.current);
      setReadingAt(t);
      const inPit = cars.filter((c) => c[3] & PIT_ANY).map((c) => c[0]);
      setPits(inPit);
      if (dataRef.current) {
        const d = dataRef.current;
        setPitVisits((prev) => pitVisitsNow(prev, inPit, d));
      }
      setUnplaced([]);
      setOverdue([]);
      setLostSignal([]);
      setOffTrack([]);
      setPitSpots(cars.filter((c) => c[3] & PIT_ANY).map((c) => ({ num: c[0], d: c[1], xy: null, road: false })));
      setNow(t);
    };
    read();
    const id = setInterval(read, TICK_MS);
    return () => clearInterval(id);
  }, [isReplay, enabled, track]);

  // Positions every 5 s while enabled and the page is visible; the clock each second.
  useEffect(() => {
    if (isReplay || !enabled || !track) return;
    let alive = true;
    const load = () => {
      if (document.visibilityState !== "visible") return;
      getPublic<{ ageMs: number; cars: CarPlace[]; pit?: PitLane | null }>(
        `/live/${encodeURIComponent(slug)}/pos`
      )
        .then(({ body, edgeAgeMs }) => {
          if (!alive) return;
          const t = Date.now();
          const age = body.ageMs + edgeAgeMs;
          const onTrack = body.cars.filter((c) => !(c[3] & PIT_ANY));
          pitLane.current = body.pit ?? null;
          motions.current = takeReading(
            motions.current,
            onTrack,
            t,
            age,
            track.len,
            typicalSpeed,
            (n) => lapPaceRef.current.get(n) ?? null,
            cautionRef.current,
            pitLane.current
          );
          setReadingAt(t - age);
          const inPit = body.cars.filter((c) => c[3] & PIT_ANY).map((c) => c[0]);
          setPits(inPit);
          if (dataRef.current) {
            const d = dataRef.current;
            setPitVisits((prev) => pitVisitsNow(prev, inPit, d));
          }
          setUnplaced(
            body.cars
              .filter((c) => !(c[3] & (PIT_ANY | OVERDUE)) && c[1] === null)
              .map((c) => c[0])
          );
          setOverdue(
            body.cars.filter((c) => !(c[3] & PIT_ANY) && c[3] & OVERDUE).map((c) => c[0])
          );
          setLostSignal(
            body.cars.filter((c) => !(c[3] & PIT_ANY) && c[3] & SIG_LOST).map((c) => c[0])
          );
          setOffTrack(
            body.cars.filter((c) => !(c[3] & PIT_ANY) && c[3] & OFF_TRACK).map((c) => c[0])
          );
          setPitSpots(
            body.cars.filter((c) => c[3] & PIT_ANY).map((c) => ({ num: c[0], d: c[1], xy: c[4] ?? null, road: !(c[3] & IN_PIT) }))
          );
        })
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      alive = false;
      clearInterval(t);
      clearInterval(tick);
    };
  }, [slug, track, typicalSpeed, enabled, isReplay]);

  // Moving the cars on: each frame on the map (a short step), else with each second's tick.
  const lastStep = useRef(Date.now());
  const stepTo = useCallback(
    (t: number, maxDtMs = FRAME_MAX_MS) => {
      if (!track) return;
      const r = replayRef.current;
      if (r) {
        // A replay: exactly where the timing puts each car now.
        motions.current = replayMotions(motions.current, r.placesAt(t), t, dataRef.current);
        lastStep.current = t;
        return;
      }
      step(motions.current, t, Math.min(maxDtMs, t - lastStep.current), track.len);
      lastStep.current = t;
    },
    [track]
  );
  useEffect(() => {
    // The map has moved them within the last half second: nothing to do.
    if (enabled && Date.now() - lastStep.current > TICK_MS / 2) stepTo(now, 2 * TICK_MS);
  }, [now, enabled, stepTo]);

  // The last flag change seen (the first flag on opening the page is none).
  const prevFlag = useRef<string | null | undefined>(undefined);
  const [flagChange, setFlagChange] = useState<FlagChange | null>(null);
  useEffect(() => {
    const ev = flagEvent(prevFlag.current, flag);
    if (ev)
      setFlagChange({
        event: ev,
        from: prevFlag.current ?? null,
        to: flag,
        at: Date.now(),
      });
    prevFlag.current = flag;
  }, [flag]);

  const out = useMemo(() => (data ? outCars(data) : new Set<number>()), [data]);
  // Early in a race or stint a car's pace comes from fewer laps, or its class's (live-battles.ts mapPaces).
  const greenPace = useMemo(() => (data ? mapPaces(data) : new Map<number, number | null>()), [data]);
  // Worked out each second (the cars move on in place in `motions`).
  const places = useMemo(
    () => (data ? carPlaces(data, motions.current, out, pitVisits) : []),
    [data, out, pitVisits, now, readingAt] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const stopped = useMemo(
    () =>
      data
        ? stoppedCars({
            data,
            motions: motions.current,
            unplaced,
            out,
            pitVisits,
            lapStopped: stoppedByLaps(data),
            now,
            len: track?.len ?? null,
          })
        : new Set<number>(),
    [data, unplaced, out, pitVisits, now, track]
  );
  const caution = isCaution(flag);
  const closingOn = useCallback(
    (key: number) =>
      track
        ? closingOnFocus({
            places,
            focusKey: key,
            len: track.len,
            pace: (k) => greenPace.get(k) ?? null,
            caution,
          })
        : [],
    [places, track, greenPace, caution]
  );

  return {
    track,
    trackMissing,
    motions,
    pitLane,
    readingAt,
    pits,
    pitVisits,
    unplaced,
    offTrack,
    pitSpots,
    lostSignal,
    overdue,
    out,
    now,
    typicalSpeed,
    places,
    greenPace,
    stopped,
    flagChange,
    closingOn,
    stepTo,
  };
}
